import { ComputedRef } from 'vue'

import { DbConstants } from '@/const'
import { IBomHeaderDTO, IBomAlternativeDTO, IBomItemDTO, BomTreeNodeType, IBomTreeNode } from '@/types/bom'
import { IMaterialDTO, MaterialListItem } from '@/types/material'
import { buildBomItemLocalization, buildClassificationLocalization, buildLocalization, DEBUG, Material } from '@/util'

export class NodeTypeUtil {
    static nodeType2name(nodeType: BomTreeNodeType): string | null {
        switch (nodeType) {
            case BomTreeNodeType.ProductionNode: return 'production-items'
            case BomTreeNodeType.MaintenanceNode: return 'maintenance-items'
            case BomTreeNodeType.AlternativeBomNode: return 'alternative-bom'
            case BomTreeNodeType.RecommendedItemsNode: return 'recommended-items'
            case BomTreeNodeType.AlternativeItemsNode: return 'alternative-items'
            case BomTreeNodeType.ReplacementNode: return 'replacement-items'
            default: return null
        }
    }

    static nodeType2ClassName(nodeType: BomTreeNodeType, classType: string): string {
        const name = NodeTypeUtil.nodeType2name(nodeType)
        return name == null
            ? ''
            : `${classType}-${name}`
    }
}

/** Used to transform an exploded BOM structure to a tree structure with appropriate information
 *  and explanatory intermediate nodes, useful for displaying in a Telerik tree list control
 */
export class BomHeaderToTreeNodeStructureTransformer {
    /********************************************************************************************************************
     PRIVATE MEMBERS
     ********************************************************************************************************************/
    private _bomItemSorter: (item: IBomItemDTO) => string
    private _idCounter: number
    private _localize: (key: string) => string
    private _localizeBomItem: (key: string) => string

    /********************************************************************************************************************
     CTOR
     ********************************************************************************************************************/
    constructor(itemSorter: (item: IBomItemDTO) => string, localize: (key: string) => string, localizeBomItem: (key: string) => string) {
        this._bomItemSorter = itemSorter
        this._localize = localize
        this._localizeBomItem = localizeBomItem
        this._idCounter = 0
    }

    /********************************************************************************************************************
     PUBLIC METHODS 
     ********************************************************************************************************************/
    /** Transforms the exploded BOM to a tree structure, that is easy to use for displaying
     *  NOTE: The result includes the specified BOM header itself as the root node, if it refers to a material
     * */
    transform(data: IBomHeaderDTO, maxRecursion?: number): IBomTreeNode[] {
        if (maxRecursion == null) {
            maxRecursion = 99
        }

        this.resetId()

        if (data.material) {
            const rootItem: IBomItemDTO = {
                ...data,
                isFixedQty: false,
                isBulkOnBomItem: false,
                isBulk: data.material.isBulk,
                qty: 1,
                unit: ''
            }

            return [this.transformBomItem(rootItem, 1, null, maxRecursion)]
        }
        else {
            return this.transformBomInstances(data.bomInstances, 1, null, maxRecursion)
        }
    }

    /********************************************************************************************************************
     PRIVATE METHODS
     ********************************************************************************************************************/
    private resetId() {
        this._idCounter = 0
    }

    private nextId(): number {
        return ++this._idCounter
    }

    // IBomAlternativeDTO[] --> IBomTreeNode[] (with children = required intermediate nodes)
    private transformBomInstances(bomInstances: IBomAlternativeDTO[], parentQty: number, parentItem: IBomItemDTO | null, level: number): IBomTreeNode[] {
        const bomUsageMap = bomInstances.toLookup(item => item.bomUsage)

        const productionBoms = bomUsageMap
            .get(DbConstants.materialBomUsage.Default)

        const replacementBoms = bomUsageMap
            .get(DbConstants.materialBomUsage.Replacement)

        const recommendedItemsBoms = bomUsageMap
            .get(DbConstants.materialBomUsage.RecommendedItems)

        const maintenanceBoms = bomUsageMap
            .get(DbConstants.materialBomUsage.Maintenance)

        const alternativeItemsBoms = bomUsageMap
            .get(DbConstants.materialBomUsage.AlternativeItems)

        const result: IBomTreeNode[] = []

        // Introduce "Simple Replacement" node?
        if (parentItem?.replacement) {
            const replacingBomItem: IBomItemDTO = {
                ...parentItem.replacement,
                isBulk: parentItem.isBulk,
                isBulkOnBomItem: parentItem.isBulkOnBomItem,
                qty: parentQty,
                unit: parentItem.unit,
                isFixedQty: parentItem.isFixedQty
            }

            const simpleReplacementNode = {
                nodeId: this.nextId(),
                nodeType: BomTreeNodeType.ReplacementNode,
                description: this.nodeType2LocalizedText(BomTreeNodeType.ReplacementNode),
                parentBomItem: parentItem,
                childNodes: [this.transformBomItem(replacingBomItem, parentQty, parentItem, level)]
            } as IBomTreeNode

            result.push(simpleReplacementNode)
        }

        // Introduce "Complex Replacement" node?
        if (replacementBoms?.length) {
            const complexReplacementNode = this.transformBomGroup(replacementBoms, BomTreeNodeType.ReplacementNode, parentQty, parentItem, level)
            result.push(complexReplacementNode)
        }

        // Introduce "Recommended Items" node?
        if (recommendedItemsBoms?.length) {
            const recommendedItemsNode = this.transformBomGroup(recommendedItemsBoms, BomTreeNodeType.RecommendedItemsNode, parentQty, parentItem, level)
            result.push(recommendedItemsNode)
        }

        // Introduce "Production" node?
        if (productionBoms?.length) {
            const productionNode = this.transformBomGroup(productionBoms, BomTreeNodeType.ProductionNode, parentQty, parentItem, level)
            result.push(productionNode)
        }

        // Introduce "Maintenance" node?
        if (maintenanceBoms?.length) {
            const maintenanceNode = this.transformBomGroup(maintenanceBoms, BomTreeNodeType.MaintenanceNode, parentQty, parentItem, level)
            result.push(maintenanceNode)
        }

        // Introduce "Alternative Items" node?
        if (alternativeItemsBoms?.length) {
            const alternativeItemsNode = this.transformBomGroup(alternativeItemsBoms, BomTreeNodeType.AlternativeItemsNode, parentQty, parentItem, level)
            result.push(alternativeItemsNode)
        }

        return result
    }

    /** Transforms a group of BOMs of identical BOM usage to a single BOM tree node of the relevant node type */
    private transformBomGroup(boms: IBomAlternativeDTO[],
        nodeType: BomTreeNodeType,
        parentQty: number,
        parentItem: IBomItemDTO | null,
        level: number): IBomTreeNode {
        let childItems: IBomTreeNode[]
        const severalAlternatives = boms.length > 1

        // Several alternatives?
        if (severalAlternatives) {
            childItems = boms.map(bom => ({
                nodeId: this.nextId(),
                nodeType: BomTreeNodeType.AlternativeBomNode,
                description: bom.bomAlternativeText
                    ? `${bom.bomAlternative} (${bom.bomAlternativeText})`
                    : `${bom.bomAlternative}`,
                childNodes: bom.components
                    .sortBy(c => this._bomItemSorter(c))
                    .flatMap(c => this.transformBomItem(c, parentQty, parentItem, level))
            } as IBomTreeNode))
        }
        else {
            childItems = boms[0].components
                .sortBy(c => this._bomItemSorter(c))
                .flatMap(c => this.transformBomItem(c, parentQty, parentItem, level))
        }

        const resultNode = {
            bomItem: null,
            nodeId: this.nextId(),
            nodeType,
            parentBomItem: parentItem,
            description: this.nodeType2LocalizedText(nodeType),
            hasSeveralAlternativeBoms: severalAlternatives,
            childNodes: childItems,
            recursionDetected: childItems.length == 0 && boms[0].recursionDetected
        } as IBomTreeNode

        return resultNode
    }

    private transformBomItem(bomItem: IBomItemDTO,
        parentQty: number,
        parentItem: IBomItemDTO | null,
        level: number): IBomTreeNode {
        const qty = bomItem.isFixedQty
            ? bomItem.qty
            : bomItem.qty * parentQty

        // transform bom instances (each type of usage will transform to an intermediate node of a specific BomTreeNodeType)
        const childNodes = level == 0
            ? []
            : this.transformBomInstances(bomItem.bomInstances, qty, bomItem, level - 1)

        const productionBomType = this.hasChildNodeOfType(childNodes, BomTreeNodeType.ProductionNode)
        const maintenanceBomType = this.hasChildNodeOfType(childNodes, BomTreeNodeType.MaintenanceNode)
        const boughtWithBomType = this.hasChildNodeOfType(childNodes, BomTreeNodeType.RecommendedItemsNode)
        const alternativeItemsBomType = this.hasChildNodeOfType(childNodes, BomTreeNodeType.AlternativeItemsNode)
        const replacementType = this.hasChildNodeOfType(childNodes, BomTreeNodeType.ReplacementNode)

        const result: IBomTreeNode = {
            nodeId: this.nextId(),
            nodeType: BomTreeNodeType.Material,
            bomItem: bomItem,
            parentBomItem: parentItem,
            qty: qty,
            unit: bomItem.unit,
            hasProductionBom: productionBomType === true,
            hasMaintenanceBom: maintenanceBomType === true,
            hasBoughtWithBom: boughtWithBomType === true,
            hasAlternativeItemsBom: alternativeItemsBomType === true,
            hasReplacement: replacementType === true,
            hasRecursiveProductionBom: productionBomType === 'recursive',
            hasRecursiveMaintenanceBom: maintenanceBomType === 'recursive',
            hasRecursiveBoughtWithBom: boughtWithBomType === 'recursive',
            hasRecursiveAlternativeItemsBom: alternativeItemsBomType === 'recursive',
            childNodes
        }

        return result
    }

    private hasChildNodeOfType(childNodes: IBomTreeNode[], targetNodeType: BomTreeNodeType): 'recursive' | true | false {
        const childNode = childNodes.find(n => n.nodeType == targetNodeType)

        if (childNode) {
            return childNode.recursionDetected
                ? 'recursive'
                : true
        }
        else {
            return false
        }
    }

    nodeType2LocalizedText(nodeType: BomTreeNodeType): string {
        switch (nodeType) {
            case BomTreeNodeType.ProductionNode:
                return this._localizeBomItem('BOMitems_Production')

            case BomTreeNodeType.MaintenanceNode:
                return this._localizeBomItem('BOMitems_Maintenance')

            case BomTreeNodeType.RecommendedItemsNode:
                return this._localizeBomItem('BOMitems_Recommended')

            case BomTreeNodeType.AlternativeItemsNode:
                return this._localizeBomItem('BOMitems_Alternatives')

            case BomTreeNodeType.ReplacementNode:
                return this._localize('Replacement')

            default:
                return `Unexpected node type '${nodeType}'`
        }
    }
}

/** Used while building a spare part category hierarchy */
type treeNode = {
    name: string,
    bomHeader?: IBomHeaderDTO,
    childNodes: Map<string, treeNode>
}

/** Used to transform a tree structure to MaterialListItem array + lookup of each 
 *  item by material number
 */
export class BomTreeNodesToMaterialItemListTransformer {
    /********************************************************************************************************************
     PRIVATE MEMBERS
     ********************************************************************************************************************/
    private _localize: (id: string) => string
    private _localizeBomItem: (id: string) => string
    private _localizePropValueHierarchy: any
    private _currentLanguage: ComputedRef<string>

    /** Used for BOM explosion of leaf nodes */
    private _bomTransformer: BomHeaderToTreeNodeStructureTransformer

    private _idCounter: number = 0

    private _nodeLookup: { [materialNo: number]: MaterialListItem } = {}

    /********************************************************************************************************************
     CTOR
     ********************************************************************************************************************/
    constructor() {
        const { currentLanguage, localize } = buildLocalization()
        const { localizePropValueHierarchy } = buildClassificationLocalization()
        const { localizeBomItemText } = buildBomItemLocalization()

        this._localize = localize
        this._localizeBomItem = localizeBomItemText
        this._currentLanguage = currentLanguage
        this._localizePropValueHierarchy = localizePropValueHierarchy

        this._bomTransformer = new BomHeaderToTreeNodeStructureTransformer(
            item => this.materialTextFor(item.material),
            localize,
            localizeBomItemText
        )
    }

    /********************************************************************************************************************
     PUBLIC METHOD
     ********************************************************************************************************************/
    /** Transforms all the specified BOM headers to tree list items */
    transform(bomData: IBomHeaderDTO[], maxRecursion?: number): { items: MaterialListItem[], lookup: { [materialNo: number]: MaterialListItem } } {
        DEBUG('transforming data...', bomData)

        this.resetId()
        this._nodeLookup = {}

        const treeRoot: treeNode = {
            name: 'root',
            childNodes: new Map()
        }

        // First build spare part hierarchy, according to category (intermediate tree structure)
        for (const bom of bomData) {
            const targetNode = this.getTargetNode(bom, treeRoot)

            const materialNode: treeNode = {
                name: this.materialTextFor(bom.material),
                bomHeader: bom,
                childNodes: new Map()
            }

            targetNode.childNodes.set(bom.material.materialNo.toString(), materialNode)
        }

        // Now transform the tree to list items
        const result = this.transformTreeToListItems(treeRoot, maxRecursion ?? 99)
        this.addParentLinks(result)

        DEBUG(result, this._nodeLookup)

        return {
            items: result,
            lookup: this._nodeLookup
        }
    }

    transformSingle(bomHeader: IBomHeaderDTO): MaterialListItem | null {
        const treeNode = this._bomTransformer.transform(bomHeader, 2)

        if (treeNode.length == 1) {
            const result = this.transformBomTreeNode(treeNode[0], 2)
            return result
        }

        return null
    }

    /********************************************************************************************************************
     PRIVATE METHODS
     ********************************************************************************************************************/
    private resetId() {
        this._idCounter = 0
    }

    private nextId(): number {
        return ++this._idCounter
    }

    /** Returns the node within the spare part hierarchy, where the specified BOM header should be added */
    private getTargetNode(bomHeader: IBomHeaderDTO, root: treeNode): treeNode {
        const material = bomHeader.material
        const nodeNames = this._localizePropValueHierarchy(
            DbConstants.sparePartPropertyNames.sparePartType,
            material.sparePartType
        )

        if (!nodeNames.length) {
            nodeNames.push(this._localize('Undefined'))
        }

        let currentNode = root

        for (const nodeName of nodeNames) {
            let childNode = currentNode.childNodes.get(nodeName)

            if (!childNode) {
                childNode = {
                    name: nodeName,
                    childNodes: new Map()
                }
                currentNode.childNodes?.set(nodeName, childNode)
            }

            currentNode = childNode
        }

        return currentNode
    }

    /** Transforms the spare part hierarchy to list items */
    private transformTreeToListItems(root: treeNode, maxRecursion: number): MaterialListItem[] {
        const result: MaterialListItem[] = []

        for (const node of Array.from(root.childNodes.values()).sortBy(n => n.name)) {

            if (node.bomHeader?.material) {
                // Add material leaf node, incl. BOM explosion
                const bomExplosionTree = this.transformBomExplosion(node.bomHeader, maxRecursion)
                const materialNode = bomExplosionTree[0]
                materialNode.leafNodeCount = 1
                this._nodeLookup[node.bomHeader?.material.materialNo] = materialNode
                result.push(materialNode)
            }
            else {
                // Add intermediate hierarchy node
                const childItems = this.transformTreeToListItems(node, maxRecursion)
                const leafCount = childItems
                    .map(item => item.leafNodeCount)
                    .reduce((a, b) => a + b, 0)

                const description = `${node.name} (${leafCount})`


                const intermediateNode = new MaterialListItem({
                    id: this.nextId(),
                    expandIconClass: '',
                    description: description,
                    childItems: childItems,
                    visibleChildItems: childItems,
                    leafNodeCount: leafCount,
                    isSparePart: false
                })

                result.push(intermediateNode)
            }
        }

        return result
    }

    /** Returns BOM explosion list items for the specified BOM header */
    private transformBomExplosion(bom: IBomHeaderDTO, maxRecursion: number): MaterialListItem[] {
        const bomTreeNodes = this._bomTransformer.transform(bom, maxRecursion)
        return this.transformBomTreeNodes(bomTreeNodes, maxRecursion)
    }

    private transformBomTreeNodes(nodes: IBomTreeNode[], maxRecursion: number): MaterialListItem[] {
        return nodes.map(node => this.transformBomTreeNode(node, maxRecursion))
    }

    private transformBomTreeNode(node: IBomTreeNode, maxRecursion: number): MaterialListItem {
        const material = node.bomItem?.material
        const childItems = this.transformBomTreeNodes(node.childNodes, maxRecursion)

        // Description
        let description = ''

        if (node.description) {
            description = node.description
        }
        else if (material) {
            description = this.materialTextFor(material)
        }

        // Status text
        let statusText = ''

        if (node.nodeType == BomTreeNodeType.ReplacementNode) {
            statusText = `${this._localize('ReplacementFor')} ${node.parentBomItem?.material.materialNo}`
        }
        else if (node.nodeType == BomTreeNodeType.Material && material) {
            statusText = this.getPropValueText(DbConstants.sparePartPropertyNames.sparePartStatus, material.sparePartStatus)
        }

        // Sales text
        const salesText = this.materialSalesTextFor(material)

        // Material documents
        const documents = node.bomItem?.material.documents

        const materialData = new MaterialListItem({
            id: this.nextId(), // renumber, as we add to another list item structure
            nodeType: node.nodeType,
            expandIconClass: this.node2IconClass(node),
            materialNo: material?.materialNo,
            description: description,
            sparePartStatus: material?.sparePartStatus,
            hiddenSparePart: material?.hiddenSparePart,
            sparePartRequiresSerialNumber: material?.sparePartRequiringSerialNumber,
            statusText: statusText,
            documents: documents,
            hasProductionBom: node.hasProductionBom,
            hasBoughtWithBom: node.hasBoughtWithBom,
            hasMaintenanceBom: node.hasMaintenanceBom,
            hasAlternativeItemsBom: node.hasAlternativeItemsBom,
            hasReplacement: node.hasReplacement,
            hasSeveralAlternativeBoms: node.hasSeveralAlternativeBoms,
            hasRecursiveProductionBom: node.hasRecursiveProductionBom,
            hasRecursiveMaintenanceBom: node.hasRecursiveMaintenanceBom,
            hasRecursiveBoughtWithBom: node.hasRecursiveBoughtWithBom,
            hasRecursiveAlternativeItemsBom: node.hasRecursiveAlternativeItemsBom,
            hasPicture: (documents?.findIndex(doc => doc.documentType === 'RES') ?? -1) >= 0,
            leafNodeCount: 0,
            isBulk: node.bomItem?.isBulk,
            isSparePart: material?.isSparePart ?? false,
            price: material?.price,
            priceQty: material?.priceQty,
            priceQtyUnit: material?.priceQtyUnit,
            qty: node.qty,
            unit: node.unit,
            sparePartPackage: this.getPropValueText(DbConstants.sparePartPropertyNames.sparePartPackage, material?.sparePartPackage),
            salesText: salesText,
            availableStock: node.bomItem?.availableStock,
            partnerMaterialNo: material?.partnerMaterialNo,
            childItems: childItems,
            visibleChildItems: maxRecursion <= 1 ? [] : childItems
        })

        return materialData
    }

    private node2IconClass(node: IBomTreeNode): string {
        const iconPath = this.nodeType2IconClass(node.nodeType)
        return iconPath
    }

    private addParentLinks(nodes: MaterialListItem[], parent?: MaterialListItem) {
        for (const node of nodes) {
            node.parentItem = parent

            if (node.childItems) {
                this.addParentLinks(node.childItems, node)
            }
        }
    }

    private nodeType2IconClass(nodeType: BomTreeNodeType): string {
        return NodeTypeUtil.nodeType2ClassName(nodeType, 'icon')
    }

    private materialTextFor(material: IMaterialDTO): string {
        const m = new Material(material)
        return m.shortText(this._currentLanguage.value)
    }

    private materialSalesTextFor(material: IMaterialDTO | undefined): string | undefined {
        if (!material) return undefined

        const m = new Material(material)
        const salesText = m.salesText(this._currentLanguage.value)
        return salesText === m.TextWhenMissing
            ? undefined
            : salesText
    }

    private getPropValueText(propertyName: string, propertyValue?: string | null): string {
        if (!propertyValue) return ''
        const propertyNames = this._localizePropValueHierarchy(propertyName, propertyValue)
        const result = propertyNames.join('/')

        return result
    }
}